import React from 'react';
import { Box, Paper, Typography, CircularProgress, Stack, TextField, IconButton, Chip } from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

// ------------------------------------------------------------
// 1. AIChatMessage
// ------------------------------------------------------------
export interface AIChatMessageProps {
  sender: 'user' | 'assistant' | 'system' | string;
  content: string;
  senderName?: string;
}

export const AIChatMessage: React.FC<AIChatMessageProps> = ({ sender, content, senderName }) => {
  const isAssistant = sender === 'assistant' || sender === 'system';
  const displayName = senderName || (isAssistant ? 'AI' : 'You');
  return (
    <Box
      sx={{
        alignSelf: isAssistant ? 'flex-start' : 'flex-end',
        maxWidth: '85%',
      }}
    >
      <Paper
        elevation={1}
        sx={{
          p: 2,
          borderRadius: 3,
          backgroundColor: isAssistant ? 'action.hover' : 'primary.main',
          color: isAssistant ? 'text.primary' : 'primary.contrastText',
          borderTopLeftRadius: isAssistant ? 0 : 3,
          borderTopRightRadius: isAssistant ? 3 : 0,
        }}
      >
        <Typography variant="caption" sx={{ display: 'block', mb: 0.5, opacity: 0.7, fontWeight: 'bold' }}>
          {displayName}
        </Typography>
        <Box
          className="markdown-content"
          sx={{
            fontSize: '0.95rem',
            lineHeight: 1.5,
            '& p': { m: 0 },
            '& ul, & ol': { pl: 2, mt: 1, mb: 1 },
            '& li': { mb: 0.5 },
            '& code': {
              fontFamily: 'monospace',
              backgroundColor: isAssistant ? 'divider' : 'rgba(0,0,0,0.2)',
              px: 0.5,
              borderRadius: 1,
            },
          }}
        >
          <ReactMarkdown remarkPlugins={[remarkGfm]}>
            {content}
          </ReactMarkdown>
        </Box>
      </Paper>
    </Box>
  );
};

// ------------------------------------------------------------
// 2. AIChatInput
// ------------------------------------------------------------
export interface AIChatInputProps {
  value: string;
  onChange: (val: string) => void;
  onSubmit: (text: string) => void;
  disabled?: boolean;
  placeholder?: string;
}

export const AIChatInput: React.FC<AIChatInputProps> = ({
  value,
  onChange,
  onSubmit,
  disabled,
  placeholder = 'Ask a question...',
}) => {
  const handleSend = () => {
    if (value.trim()) {
      onSubmit(value.trim());
    }
  };

  return (
    <Stack direction="row" spacing={1.5} alignItems="center">
      <TextField
        fullWidth
        variant="outlined"
        size="small"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        autoComplete="off"
        disabled={disabled}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            handleSend();
          }
        }}
        sx={{
          '& .MuiOutlinedInput-root': {
            borderRadius: 3,
          },
        }}
      />
      <IconButton
        onClick={handleSend}
        disabled={!value.trim() || disabled}
        color="primary"
      >
        <SendIcon />
      </IconButton>
    </Stack>
  );
};

// ------------------------------------------------------------
// 3. AIChatSuggestions
// ------------------------------------------------------------
export interface AIChatSuggestionChip {
  label: string;
  text: string;
}

export interface AIChatSuggestionsProps {
  chips: AIChatSuggestionChip[];
  onClick: (text: string) => void;
  disabled?: boolean;
}

export const AIChatSuggestions: React.FC<AIChatSuggestionsProps> = ({ chips, onClick, disabled }) => {
  if (chips.length === 0) return null;
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
      {chips.map((chip, i) => (
        <Chip
          key={i}
          label={chip.label}
          size="small"
          onClick={() => !disabled && onClick(chip.text)}
          sx={{ cursor: disabled ? 'not-allowed' : 'pointer' }}
          color="primary"
          variant="outlined"
          disabled={disabled}
        />
      ))}
    </Box>
  );
};

// ------------------------------------------------------------
// 4. AIChatLoader
// ------------------------------------------------------------
export interface AIChatLoaderProps {
  label?: string;
}

export const AIChatLoader: React.FC<AIChatLoaderProps> = ({ label = 'AI is thinking...' }) => {
  return (
    <Box sx={{ alignSelf: 'flex-start', maxWidth: '85%' }}>
      <Paper elevation={1} sx={{ p: 2, borderRadius: 3, backgroundColor: 'action.hover' }}>
        <Typography variant="caption" sx={{ display: 'block', mb: 1, opacity: 0.7, fontWeight: 'bold' }}>
          {label}
        </Typography>
        <CircularProgress size={20} />
      </Paper>
    </Box>
  );
};
